import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Snackbar,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';

import CalendarShareGuide from '../../components/LeadsVo/CalendarShareGuide';
import { useLeadsVoAgent, useUpdateCalendar } from '../../hooks/useLeadsVoQueries';

// Page de gestion du calendrier Google (/leads-vo/calendrier), accessible depuis le
// menu « RDV Qualifiés » : état de la connexion, connexion ou remplacement via le
// guide, et déconnexion. Reste envoyable par lien direct aux agences qui ont passé
// l'étape pendant l'onboarding.
const LeadsVoCalendar = () => {
  const { data: agentData, isLoading } = useLeadsVoAgent();
  const updateCalendar = useUpdateCalendar();

  const [calendarId, setCalendarId] = useState('');
  const [error, setError] = useState('');
  const [feedback, setFeedback] = useState<'connected' | 'disconnected' | null>(null);
  const [prefilled, setPrefilled] = useState(false);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);

  const partnerCalendarId = agentData?.partner?.google_cal_calendar_id ?? '';
  const alreadyConnected = Boolean(partnerCalendarId);

  useEffect(() => {
    if (prefilled || !partnerCalendarId) return;
    setCalendarId(partnerCalendarId);
    setPrefilled(true);
  }, [partnerCalendarId, prefilled]);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress sx={{ color: '#FDBB5F' }} />
      </Box>
    );
  }

  // Pas encore provisionné chez le partenaire : la connexion calendrier passe
  // par le parcours d'onboarding complet.
  if (!agentData?.leads_vo_agent_id) {
    return <Navigate to="/leads-vo/onboarding" replace />;
  }

  const describeError = (err: any): string => {
    const raw = err.response?.data;
    if (raw?.error === 'calendar_not_shared' || err.response?.status === 409) {
      return "Le calendrier n'est pas accessible. Vérifiez que vous avez bien partagé votre calendrier avec l'adresse email indiquée ci-dessus, avec la permission « Apporter des modifications aux événements ».";
    }
    const msg = raw?.detail || raw?.error || raw?.message || 'Une erreur est survenue';
    return typeof msg === 'string' ? msg : JSON.stringify(msg);
  };

  const handleSave = async () => {
    if (!calendarId.trim()) {
      setError("Collez l'identifiant de votre calendrier avant de valider.");
      return;
    }
    try {
      setError('');
      await updateCalendar.mutateAsync(calendarId);
      setFeedback('connected');
    } catch (err: any) {
      setError(describeError(err));
    }
  };

  // Déconnexion = identifiant vide transmis au partenaire ; les prochains RDV ne
  // seront plus ajoutés à l'agenda de l'agence.
  const handleDisconnect = async () => {
    setConfirmDisconnect(false);
    try {
      setError('');
      await updateCalendar.mutateAsync('');
      setCalendarId('');
      setFeedback('disconnected');
    } catch (err: any) {
      setError(describeError(err));
    }
  };

  const isReplacing = alreadyConnected && calendarId.trim() !== partnerCalendarId;
  const saveLabel = alreadyConnected ? 'Remplacer mon calendrier' : 'Connecter mon calendrier';

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography sx={{ fontWeight: 900, fontSize: 28, color: '#262626', mb: 1 }}>Mon calendrier</Typography>
      <Typography sx={{ color: '#464646', fontSize: 14, mb: 4 }}>
        Connectez votre agenda Google pour que vos rendez-vous qualifiés s'y ajoutent automatiquement. Vous pouvez le
        remplacer ou le déconnecter à tout moment.
      </Typography>

      {/* État actuel de la connexion */}
      <Paper elevation={3} sx={{ p: 3, borderRadius: 2, mb: 3 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
          <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
            <Chip
              label={alreadyConnected ? 'Connecté' : 'Non connecté'}
              size="small"
              sx={{
                fontWeight: 700,
                ...(alreadyConnected
                  ? { backgroundColor: '#E8F5E9', color: '#2E7D32' }
                  : { backgroundColor: '#F5F5F5', color: '#757575' }),
              }}
            />
            {alreadyConnected ? (
              <Typography sx={{ fontFamily: 'monospace', fontSize: 13, color: '#262626', wordBreak: 'break-all' }}>
                {partnerCalendarId}
              </Typography>
            ) : (
              <Typography sx={{ fontSize: 14, color: '#464646' }}>
                Aucun calendrier connecté : vos RDV ne sont pas ajoutés à votre agenda.
              </Typography>
            )}
          </Box>
          {alreadyConnected && (
            <Button
              onClick={() => setConfirmDisconnect(true)}
              disabled={updateCalendar.isPending}
              sx={{ textTransform: 'none', color: '#C62828', fontWeight: 600 }}
            >
              Déconnecter
            </Button>
          )}
        </Box>
      </Paper>

      <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 18, color: '#262626', mb: 3 }}>
          {alreadyConnected ? 'Remplacer par un autre calendrier' : 'Connecter mon calendrier'}
        </Typography>
        <CalendarShareGuide calendarId={calendarId} setCalendarId={setCalendarId} error={error} />
        <Box mt={3} display="flex" justifyContent="flex-end">
          <Button
            onClick={handleSave}
            disabled={updateCalendar.isPending || (alreadyConnected && !isReplacing)}
            sx={{
              backgroundColor: '#FDBB5F',
              color: '#262626',
              textTransform: 'none',
              fontWeight: 600,
              px: 4,
              '&:hover': { backgroundColor: '#fcae4d' },
              '&:disabled': { opacity: 0.5 },
            }}
          >
            {updateCalendar.isPending ? <CircularProgress size={20} sx={{ color: '#262626' }} /> : saveLabel}
          </Button>
        </Box>
      </Paper>

      <Dialog open={confirmDisconnect} onClose={() => setConfirmDisconnect(false)}>
        <DialogTitle sx={{ fontWeight: 700 }}>Déconnecter le calendrier ?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Vos prochains rendez-vous qualifiés ne seront plus ajoutés à votre agenda Google. Vous pourrez reconnecter
            un calendrier à tout moment.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDisconnect(false)} sx={{ textTransform: 'none', color: '#464646' }}>
            Annuler
          </Button>
          <Button onClick={handleDisconnect} sx={{ textTransform: 'none', color: '#C62828', fontWeight: 700 }}>
            Déconnecter
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={feedback !== null}
        autoHideDuration={6000}
        onClose={() => setFeedback(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setFeedback(null)}>
          {feedback === 'disconnected'
            ? 'Calendrier déconnecté. Vos RDV ne seront plus ajoutés à votre agenda.'
            : "Calendrier connecté ! Vos prochains RDV qualifiés s'y ajouteront automatiquement."}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default LeadsVoCalendar;
